import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Artist, Song, Genre } from "../types/type";
import { generateSongId } from "../api/fetch.api";
import { addNewSong } from "../api/write.api"; // 스키마 타입 임포트
import { artistListState, totalThumbnailUrlListState } from "../atom/atom";
import { useRecoilValue } from "recoil";
import { useThumbnail } from "../hooks/useThumbnail";

interface AddSongFormProps {
  initialData: Pick<Song, "url"> & Partial<Omit<Song, "url">>; // Make url required
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (songId: string) => void;
}

interface FormDataType {
  title: string;
  artistId: string;
  artistName: string;
  thumbnailUrl: string;
  youtubeId: string;
  tagList: string[];
  isAI: boolean;
  makerUserId: string;
  url: string; // Required audio URL
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
}

const AddSongForm: React.FC<AddSongFormProps> = ({
  initialData,
  isOpen = false,
  onClose = () => {},
  onSuccess = () => {},
}) => {
  // Form data state
  const [formData, setFormData] = useState<FormDataType>({
    title: "",
    artistId: "",
    artistName: "",
    thumbnailUrl: "",
    youtubeId: "",
    tagList: ["homebrewRecommend"],
    isAI: true,
    makerUserId: "Homebrew",
    tag: {
      genreList: [],
      tagList: ["homebrewRecommend"],
    },
    ...initialData,
  });

  // UI states
  const [selectedArtistThumbnails, setSelectedArtistThumbnails] = useState<
    string[]
  >([]);
  const [newTag, setNewTag] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextSongId, setNextSongId] = useState<string>("");

  const artistList = useRecoilValue(artistListState);
  const totalThumbnailUrlList = useRecoilValue(totalThumbnailUrlListState);

  const { fetchAllArtistThumbnailList, refetchArtistThumbnailList } =
    useThumbnail();
  const [isRefetching, setIsRefetching] = useState(false);

  useEffect(() => {
    if (artistList.length && !totalThumbnailUrlList.length) {
      fetchAllArtistThumbnailList();
    }
  }, [artistList.length, totalThumbnailUrlList.length]);

  // Load thumbnails when artist is selected
  useEffect(() => {
    const loadThumbnails = async () => {
      if (formData.artistName) {
        const artist = artistList.find(
          (a) => a.artistName === formData.artistName
        );
        if (artist) handleArtistSelect(artist);
      } else if (formData.artistId) {
        const artist = artistList.find((a) => a.artistId === formData.artistId);
        if (artist) {
          handleArtistSelect(artist);
        }
      }
    };

    loadThumbnails();
  }, [
    formData.artistName,
    formData.artistId,
    totalThumbnailUrlList,
    artistList,
  ]);

  const handleRefetchThumbnails = async () => {
    setIsRefetching(true);
    try {
      await refetchArtistThumbnailList();
      // If an artist is already selected, reload their thumbnails
      if (formData.artistName) {
        const artist = artistList.find(
          (a) => a.artistName === formData.artistName
        );
        if (artist) handleArtistSelect(artist);
      }
    } catch (error) {
      console.error("Error refetching thumbnails:", error);
      alert("Failed to refetch thumbnails. Please try again.");
    } finally {
      setIsRefetching(false);
    }
  };

  // Generate next song ID
  useEffect(() => {
    const fetchNextSongId = async () => {
      const id = await generateSongId();
      setNextSongId(id);
    };
    fetchNextSongId();
  }, []);

  const handleArtistSelect = (artist: Artist) => {
    setFormData((prev) => ({
      ...prev,
      artistId: artist.artistId,
      artistName: artist.artistName,
      tagList: [artist.artistName],
      tag: {
        ...prev.tag,
        tagList: [artist.artistName],
      },
    }));
    setSelectedArtistThumbnails(totalThumbnailUrlList[artist.artistName] || []);
  };

  const handleGenreToggle = (genre: Genre) => {
    setFormData((prev) => ({
      ...prev,
      tag: {
        ...prev.tag,
        genreList: prev.tag.genreList.includes(genre)
          ? prev.tag.genreList.filter((g) => g !== genre)
          : [...prev.tag.genreList, genre],
      },
    }));
  };

  const handleAddTag = () => {
    if (!newTag) return;
    setFormData((prev) => ({
      ...prev,
      tagList: [...prev.tagList, newTag],
      tag: {
        ...prev.tag,
        tagList: [...prev.tag.tagList, newTag],
      },
    }));
    setNewTag("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.artistId || !formData.thumbnailUrl) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const songId = await addNewSong({
        artistId: formData.artistId,
        artistName: formData.artistName,
        title: formData.title,
        thumbnailUrl: formData.thumbnailUrl,
        youtubeId: formData.youtubeId,
        tagList: formData.tagList,
        isAI: formData.isAI,
        makerUserId: formData.makerUserId,
        tag: formData.tag,
        url: formData.url,
      });

      console.log({
        artistId: formData.artistId,
        artistName: formData.artistName,
        title: formData.title,
        thumbnailUrl: formData.thumbnailUrl,
        youtubeId: formData.youtubeId,
        tagList: formData.tagList,
        isAI: formData.isAI,
        makerUserId: formData.makerUserId,
        tag: formData.tag,
        url: formData.url,
      });

      onSuccess(songId);
      onClose();

      // Reset form
      setFormData({
        title: "",
        artistId: "",
        artistName: "",
        thumbnailUrl: "",
        youtubeId: "",
        tagList: ["homebrewRecommend"],
        isAI: true,
        makerUserId: "Homebrew",
        url: initialData.url,
        tag: {
          genreList: [],
          tagList: ["homebrewRecommend"],
        },
      });
    } catch (error) {
      console.error("Error adding song:", error);
      alert("Failed to add song. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <h2>Add New Song</h2>

        <div>Next Song ID: {nextSongId}</div>
        <div>url: {initialData.url}</div>
        <Form onSubmit={handleSubmit}>
          {/* Artist Selection */}
          <FormSection>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Label>Thumbnails</Label>
              <button onClick={handleRefetchThumbnails} disabled={isRefetching}>
                {isRefetching ? "Refreshing..." : "Refresh Thumbnails"}
              </button>
            </div>
            <Label>Select Artist</Label>
            <ArtistGrid>
              {artistList.map((artist) => (
                <ArtistButton
                  key={artist.artistId}
                  type="button"
                  selected={formData.artistId === artist.artistId}
                  onClick={() => handleArtistSelect(artist)}
                >
                  {artist.artistName}
                </ArtistButton>
              ))}
            </ArtistGrid>
          </FormSection>

          {/* Title Input */}
          <FormSection>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />
          </FormSection>

          {/* Genre Selection */}
          <FormSection>
            <Label>Genre</Label>
            <ArtistGrid>
              {Object.values(Genre).map((genre) => (
                <ArtistButton
                  key={genre}
                  type="button"
                  selected={formData.tag.genreList.includes(genre)}
                  onClick={() => handleGenreToggle(genre)}
                >
                  {genre}
                </ArtistButton>
              ))}
            </ArtistGrid>
          </FormSection>

          {/* Tags Input */}
          <FormSection>
            <Label>Tags</Label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add new tag..."
              />
              <button type="button" onClick={handleAddTag}>
                Add
              </button>
            </div>
            <TagContainer>
              {formData.tag.tagList.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagContainer>
          </FormSection>

          {/* Thumbnail Selection */}
          <FormSection>
            <Label>Thumbnails</Label>
            <Input
              value={formData.thumbnailUrl}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  thumbnailUrl: e.target.value,
                }))
              }
              placeholder="Enter thumbnail URL..."
            />
            <ThumbnailGrid>
              {selectedArtistThumbnails.length > 0 ? (
                selectedArtistThumbnails.map((url, index) => (
                  <ThumbnailCard
                    key={index}
                    selected={formData.thumbnailUrl === url}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, thumbnailUrl: url }))
                    }
                  >
                    <ThumbnailImage src={url} alt={`Thumbnail ${index}`} />
                    {formData.thumbnailUrl === url && (
                      <SelectedMark>✓</SelectedMark>
                    )}
                  </ThumbnailCard>
                ))
              ) : formData.artistName ? (
                <p>No thumbnails available for {formData.artistName}</p>
              ) : (
                <p>Select an artist to view thumbnails</p>
              )}
            </ThumbnailGrid>
          </FormSection>

          {/* AI Checkbox */}
          <FormSection>
            <CheckboxContainer>
              <input
                type="checkbox"
                id="isAI"
                checked={formData.isAI}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isAI: e.target.checked }))
                }
              />
              <Label htmlFor="isAI">Is AI Generated</Label>
            </CheckboxContainer>
          </FormSection>

          {/* Submit Button */}
          <SubmitButton
            type="submit"
            disabled={
              isSubmitting ||
              !formData.title ||
              !formData.artistId ||
              !formData.thumbnailUrl
            }
          >
            {isSubmitting ? "Adding Song..." : "Add Song"}
          </SubmitButton>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover {
    color: #666;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: #0066cc;
  }
`;

const ArtistGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

interface ArtistButtonProps {
  selected: boolean;
}

const ArtistButton = styled.button<ArtistButtonProps>`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: ${(props) => (props.selected ? "#0066cc" : "white")};
  color: ${(props) => (props.selected ? "white" : "black")};
  cursor: pointer;
  &:hover {
    background: ${(props) => (props.selected ? "#0066cc" : "#f5f5f5")};
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

interface ThumbnailCardProps {
  selected: boolean;
}

const ThumbnailCard = styled.div<ThumbnailCardProps>`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    border-color: #0066cc;
  }

  ${(props) =>
    props.selected &&
    `
    border-color: #0066cc;
    border-width: 2px;
  `}
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
`;

const SelectedMark = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #0066cc;
  color: white;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #0066cc;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-end;

  &:hover {
    background: #0052a3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

export default AddSongForm;
